import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { App, Card, Descriptions, Table, Button, Spin, Result, Space, Tag } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { QUOTE_STATUS_COLORS, getQuoteStatusLabel } from '../utils/quoteUtils';
import { PAYMENT_STATUS_COLORS, getPaymentStatusLabel, FULFILLMENT_STATUS_COLORS, getFulfillmentStatusLabel } from '../utils/orderUtils';
import { formatCurrency, formatDate } from '../utils/format';

interface Address {
  id: string;
  address_1: string;
  address_2?: string;
  city: string;
  province?: string;
  postal_code: string;
  country_code: string;
}

interface CustomerDetailData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  created_at: string;
  addresses?: Address[];
  groups?: Array<{ id: string; name: string }>;
  orders?: Array<{
    id: string;
    display_id: string;
    total: number;
    currency_code: string;
    payment_status: string;
    fulfillment_status: string;
    created_at: string;
  }>;
}

const CustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const [customer, setCustomer] = useState<CustomerDetailData | null>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [quotes, setQuotes] = useState<any[]>([]);
  const [geoLocations, setGeoLocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    loadCustomer();
  }, [id]);

  const loadCustomer = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/admin/customers/${id}`);
      setCustomer(response.data.customer || response.data);

      try {
        const ordersResponse = await apiClient.get('/admin/orders', {
          params: { customer_id: id, limit: 50 },
        });
        setOrders(ordersResponse.data.orders || []);
      } catch (orderErr) {
        setOrders([]);
      }

      try {
        const quotesResponse = await apiClient.get('/admin/quotes', {
          params: { customer_id: id, limit: 50 },
        });
        setQuotes(quotesResponse.data.quotes || []);
      } catch (quoteErr) {
        setQuotes([]);
      }

      try {
        const geoResponse = await apiClient.get('/admin/geo-locations', {
          params: { customer_id: id, limit: 100 },
        });
        setGeoLocations(geoResponse.data.geo_locations || []);
      } catch (geoErr) {
        setGeoLocations([]);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        message.error(error.response?.data?.message || t('common.noData'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (notFound || !customer) {
    return (
      <Result
        status="404"
        title="404"
        subTitle={t('common.noData')}
        extra={
          <Button type="primary" onClick={() => navigate('/customers')}>
            {t('common.back')}
          </Button>
        }
      />
    );
  }

  const addressColumns = [
    {
      title: t('common.address'),
      key: 'address',
      render: (_: any, record: Address) => {
        const parts = [record.address_1, record.address_2, record.city, record.province, record.postal_code];
        if (!parts.filter(Boolean).length) return t('common.noAddress');
        return [...parts, record.country_code].filter(Boolean).join(', ');
      },
    },
  ];

  const orderColumns = [
    {
      title: t('orders.displayId'),
      key: 'display_id',
      render: (_: any, record: any) => (
        <a onClick={() => navigate(`/orders/${record.id}`)}>
          {record.display_id || record.id}
        </a>
      ),
    },
    {
      title: t('orders.total'),
      key: 'total',
      render: (_: any, record: any) =>
        formatCurrency(record.total, record.currency_code, i18n.language),
    },
    {
      title: t('orders.paymentStatus'),
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (status: string) => (
        <Tag color={PAYMENT_STATUS_COLORS[status] || 'default'}>
          {getPaymentStatusLabel(status, t)}
        </Tag>
      ),
    },
    {
      title: t('orders.fulfillmentStatus'),
      dataIndex: 'fulfillment_status',
      key: 'fulfillment_status',
      render: (status: string) => (
        <Tag color={FULFILLMENT_STATUS_COLORS[status] || 'blue'}>
          {getFulfillmentStatusLabel(status, t)}
        </Tag>
      ),
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatDate(date, i18n.language),
    },
  ];

  const quoteColumns = [
    {
      title: t('quotes.displayId'),
      key: 'display_id',
      render: (_: any, record: any) => record.quote_number || record.draft_order?.display_id || '-',
    },
    {
      title: t('quotes.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={QUOTE_STATUS_COLORS[status] || 'default'}>
          {getQuoteStatusLabel(status, t)}
        </Tag>
      ),
    },
    {
      title: t('quotes.serviceProvider'),
      key: 'service_provider',
      render: (_: any, record: any) => record.service_provider?.name || '-',
    },
    {
      title: t('quotes.total'),
      key: 'total',
      render: (_: any, record: any) => {
        const total = record.draft_order?.total;
        if (total === undefined || total === null) return '-';
        return formatCurrency(total, record.draft_order?.currency_code, i18n.language);
      },
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatDate(date, i18n.language),
    },
  ];

  const geoLocationTypeLabelMap: Record<string, string> = {
    Point: t('geoLocations.typePoint'),
    Polygon: t('geoLocations.typePolygon'),
    GeometryCollection: t('geoLocations.typeGeometryCollection'),
  };

  const geoLocationColumns = [
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('geoLocations.type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag color="blue">{geoLocationTypeLabelMap[type] || type}</Tag>,
    },
    {
      title: t('geoLocations.locationNumber'),
      dataIndex: 'location_number',
      key: 'location_number',
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/customers')}>
          {t('common.back')}
        </Button>
      </Space>

      <Card title={t('customers.title')} style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label={t('customers.firstName')}>{customer.first_name}</Descriptions.Item>
          <Descriptions.Item label={t('customers.lastName')}>{customer.last_name}</Descriptions.Item>
          <Descriptions.Item label={t('customers.email')}>{customer.email}</Descriptions.Item>
          <Descriptions.Item label={t('customers.phone')}>{customer.phone || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('common.createdAt')}>
            {formatDate(customer.created_at, i18n.language)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('customers.addresses')} style={{ marginBottom: 16 }}>
        <Table
          columns={addressColumns}
          dataSource={customer.addresses || []}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: t('customers.noAddresses') }}
        />
      </Card>

      <Card title={t('orders.title')} style={{ marginBottom: 16 }}>
        <Table
          columns={orderColumns}
          dataSource={orders}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: t('common.noData') }}
          onRow={(record) => ({
            onClick: () => navigate(`/orders/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Card title={t('quotes.title')} style={{ marginBottom: 16 }}>
        <Table
          columns={quoteColumns}
          dataSource={quotes}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: t('common.noData') }}
          onRow={(record) => ({
            onClick: () => navigate(`/quotes/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Card title={t('menu.geoLocations')}>
        <Table
          columns={geoLocationColumns}
          dataSource={geoLocations}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: t('common.noData') }}
        />
      </Card>
    </div>
  );
};

export default CustomerDetail;
